import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from 'shared/hooks/useAuth';
import { formatEnumLabel } from '../../../constants/enums';
import MonitoringReviewDialog, { emptyMonitoringReviewRow } from '../../../shared/MonitoringReviewDialog';
import { apiErrorMessage } from '../../../utils/responseHelpers';
import { persistWizardNestedCollection } from '../../../utils/persistWizardNestedCollection';

const RISK_COLORS = {
  LOW: { bg: '#E8F5E9', color: '#1B5E20' },
  MEDIUM: { bg: '#FFF8E1', color: '#F57F17' },
  HIGH: { bg: '#FBE9E7', color: '#BF360C' },
  CRITICAL: { bg: '#FFEBEE', color: '#B71C1C' },
};

const APPROVAL_STATUS_COLORS = {
  full: { bg: '#E8F5E9', color: '#1B5E20' },
  partial: { bg: '#FFF8E1', color: '#F57F17' },
  declined: { bg: '#FFEBEE', color: '#B71C1C' },
  none: { bg: '#ECEFF1', color: '#455A64' },
};

function slotRole(slot) {
  return slot === 1 ? '1st Approval' : '2nd Approval';
}

function findSlotApproval(approvals, slot) {
  const role = slotRole(slot);
  return (approvals ?? []).find((a) => a?.approverRole === role) || null;
}

/** Pull the free-text decline/approve note from stored approvalComments. */
function extractApprovalComment(approvalComments) {
  if (!approvalComments?.trim()) return '';
  const match = String(approvalComments).match(/\|\s*Comment:\s*([\s\S]+)$/i);
  if (match) return match[1].trim();
  return String(approvalComments).trim();
}

/** Declined slots with comments — shown under Approval for implementers. */
function getDeclinedComments(approvals) {
  return (approvals ?? [])
    .filter((a) => a?.approvalStatus === 'REJECTED')
    .map((a) => ({
      role: a.approverRole || 'Approver',
      comment: extractApprovalComment(a.approvalComments),
    }))
    .filter((row) => row.comment);
}

/** Summarize 1st/2nd approval state for the monitoring-review table. */
function getApprovalSummary(approvals) {
  const first = findSlotApproval(approvals, 1);
  const second = findSlotApproval(approvals, 2);
  const firstApproved = first?.approvalStatus === 'APPROVED';
  const secondApproved = second?.approvalStatus === 'APPROVED';
  const anyDeclined = [first, second].some((a) => a?.approvalStatus === 'REJECTED');
  const declinedComments = getDeclinedComments(approvals);

  if (firstApproved && secondApproved) {
    return { key: 'full', label: 'Fully approved', declinedComments: [] };
  }
  if (anyDeclined) {
    return { key: 'declined', label: 'Declined', declinedComments };
  }
  if (firstApproved || secondApproved) {
    const pending = firstApproved ? '2nd' : '1st';
    return { key: 'partial', label: `Awaiting ${pending} approval`, declinedComments: [] };
  }
  return { key: 'none', label: 'Not approved', declinedComments: [] };
}

/**
 * Monitoring & Review step: summary table + add/edit dialog; view opens document page.
 */
export default function WizardStepMonitoringReview({
  values,
  onSetField,
  onBeforeView,
  softwareName = '',
  softwareId = null,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primary = theme.palette.primary.main;
  const navigate = useNavigate();
  const { user } = useAuth();
  const reviewerName = user?.data?.name?.trim() || '';

  const entries = values?.entries ?? [];

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openCreate = () => {
    if (!softwareId) {
      toast.error('Save system identification first so monitoring reviews can be linked to this software.');
      return;
    }
    setDialogMode('create');
    setEditing(emptyMonitoringReviewRow(reviewerName));
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setDialogMode('edit');
    setEditing({ ...row, reviewer: row.reviewer || reviewerName });
    setDialogOpen(true);
  };

  const openView = (row) => {
    onBeforeView?.();
    navigate('/system-register/monitoring-reviews/view', {
      state: {
        review: row,
        softwareName,
        softwareId: softwareId ?? null,
        returnTo: `${window.location.pathname}${window.location.search}`,
      },
    });
  };

  const persistEntries = async (nextEntries) => {
    const persisted = await persistWizardNestedCollection(
      softwareId,
      'monitoringReviews',
      nextEntries
    );
    onSetField('entries', persisted);
    return persisted;
  };

  const handleSave = async (row) => {
    const next = [...entries];
    const idx = next.findIndex((r) => r.key === row.key);
    if (idx >= 0) {
      next[idx] = row;
    } else {
      next.push(row);
    }

    setSaving(true);
    try {
      await persistEntries(next);
      toast.success(
        dialogMode === 'edit'
          ? 'Monitoring review updated.'
          : 'Monitoring review saved and linked to this software.'
      );
      setDialogOpen(false);
      setEditing(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save monitoring review.'));
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (key) => {
    if (!softwareId) {
      onSetField(
        'entries',
        entries.filter((r) => r.key !== key)
      );
      setDeleteTarget(null);
      return;
    }
    setSaving(true);
    try {
      await persistEntries(entries.filter((r) => r.key !== key));
      toast.success('Monitoring review removed.');
      setDeleteTarget(null);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to remove monitoring review.'));
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (row) => {
    setDeleteTarget(row);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    await handleDelete(deleteTarget.key);
  };

  const riskChipSx = useMemo(
    () => (rating) => {
      const palette = RISK_COLORS[rating] || { bg: alpha(primary, 0.08), color: theme.palette.text.primary };
      return {
        bgcolor: isDark ? alpha(palette.color, 0.25) : palette.bg,
        color: isDark ? '#fff' : palette.color,
        fontWeight: 700,
      };
    },
    [isDark, primary, theme.palette.text.primary]
  );

  const approvalChipSx = useMemo(
    () => (key) => {
      const palette = APPROVAL_STATUS_COLORS[key] || APPROVAL_STATUS_COLORS.none;
      return {
        bgcolor: isDark ? alpha(palette.color, 0.28) : palette.bg,
        color: isDark ? '#fff' : palette.color,
        fontWeight: 700,
      };
    },
    [isDark]
  );

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={0.5}>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="secondary"
          startIcon={<AddIcon />}
          onClick={openCreate}
          disabled={saving}
        >
          Add Software Monitoring And Review
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(primary, 0.18),
          bgcolor: isDark ? 'background.paper' : alpha('#fff', 0.9),
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow
              sx={{
                bgcolor: alpha(primary, isDark ? 0.18 : 0.08),
                '& th': { fontWeight: 700, whiteSpace: 'nowrap' },
              }}
            >
              <TableCell>Review Area / Control</TableCell>
              <TableCell>Risk Rating</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Next review</TableCell>
              <TableCell>Approval status</TableCell>
              <TableCell align="right" width={140}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" py={1.5}>
                    No software monitoring and reviews yet. Click Add Software Monitoring And Review to link one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              entries.map((row) => {
                const approval = getApprovalSummary(row.approvals);
                return (
                  <TableRow key={row.key} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {row.reviewAreaControl || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatEnumLabel(row.monitoringType)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.riskRating ? (
                        <Chip size="small" label={formatEnumLabel(row.riskRating)} sx={riskChipSx(row.riskRating)} />
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>{row.status ? formatEnumLabel(row.status) : '—'}</TableCell>
                    <TableCell>{row.nextReviewDate || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280, verticalAlign: 'top' }}>
                      <Chip size="small" label={approval.label} sx={approvalChipSx(approval.key)} />
                      {approval.declinedComments?.length > 0 ? (
                        <Box mt={0.75} display="flex" flexDirection="column" gap={0.5}>
                          {approval.declinedComments.map((item) => (
                            <Box key={`${row.key}-${item.role}`}>
                              <Typography
                                variant="caption"
                                display="block"
                                color="error.main"
                                fontWeight={700}
                              >
                                {item.role} — for implementation
                              </Typography>
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.35 }}
                              >
                                {item.comment}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View document">
                        <IconButton size="small" onClick={() => openView(row)} disabled={saving}>
                          <VisibilityOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit review / status">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEdit(row)}
                          disabled={saving}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete review">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => askDelete(row)}
                          disabled={saving}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <MonitoringReviewDialog
        open={dialogOpen}
        mode={dialogMode}
        initialValues={editing}
        reviewerName={reviewerName}
        saving={saving}
        selectFromRegister
        excludeMonitoringReviewIds={entries
          .filter(
            (r) =>
              r?.monitoringReviewId != null
              && r?.key !== editing?.key
              && r?.id !== editing?.id
          )
          .map((r) => r.monitoringReviewId)}
        onClose={() => {
          if (saving) return;
          setDialogOpen(false);
          setEditing(null);
        }}
        onSave={handleSave}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={saving ? undefined : () => setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete monitoring review?</DialogTitle>
        <DialogContent>
          <Typography>Do you want to delete the monitoring review?</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={saving}>
            {saving ? 'Deleting…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
